import { SingleSpouseType } from 'apps/user/types/on-boarding.type';
import { useUpdateSpouse } from 'common/queries-and-mutations/spouse';
import Button from 'components/Form/Button';
import GenderField from 'components/Form/GenderField';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import { Alert } from 'components/Toast';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Spouse } from 'service/dto/spouse.dto';
import * as Yup from 'yup';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  spouse: Spouse;
};

export default function EditSpouseModal({
  onClose,
  onSuccess,
  spouse
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useUpdateSpouse();

  const formik = useFormik({
    initialValues: {
      spouseFirstName: spouse.spouseFirstName,
      spouseMiddleName: spouse.spouseMiddleName,
      spouseLastName: spouse.spouseLastName,
      homeAddress: spouse.homeAddress,
      emailAddress: spouse.emailAddress,
      phoneNumber: spouse.phoneNumber,
      gender: spouse.gender,
      isBeneficiary: !!spouse.beneficiaryId
    },
    validationSchema: Yup.object({
      spouseFirstName: Yup.string()
        .required('Spouse First Name is required')
        .nullable(),
      // spouseMiddleName: Yup.string()
      //   .required('Spouse Middle Name is required')
      //   .nullable(),
      spouseLastName: Yup.string()
        .required('Spouse Last Name is required')
        .nullable(),
      homeAddress: Yup.string().required('Home address is required').nullable(),
      phoneNumber: Yup.string().required('Phone number is required').nullable(),
      emailAddress: Yup.string()
        .email('Invalid email')
        .required('Email is required')
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSpouseSubmit(values);
    }
  });

  const onSpouseSubmit = (formValue: SingleSpouseType) => {
    mutate({ spouse: formValue, spouseId: spouse.id });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Spouse Successfully Updated" type="success" />);
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, onSuccess]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Edit Spouse</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          <div className="my-8">
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`spouseFirstName`}
                  placeholder=""
                  value={formik.values.spouseFirstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Spouse First Name"
                  error={
                    formik.touched.spouseFirstName
                      ? formik.errors.spouseFirstName
                      : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  isRequired
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`spouseMiddleName`}
                  placeholder=""
                  value={formik.values.spouseMiddleName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Spouse Middle Name"
                  error={
                    formik.touched.spouseMiddleName
                      ? formik.errors.spouseMiddleName
                      : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`spouseLastName`}
                  placeholder=""
                  value={formik.values.spouseLastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Spouse Last Name"
                  error={
                    formik.touched.spouseLastName
                      ? formik.errors.spouseLastName
                      : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  isRequired
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`homeAddress`}
                  placeholder=""
                  value={formik.values.homeAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Home Address"
                  error={
                    formik.touched.homeAddress ? formik.errors.homeAddress : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  isRequired
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`emailAddress`}
                  placeholder=""
                  value={formik.values.emailAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Email Address"
                  error={
                    formik.touched.emailAddress
                      ? formik.errors.emailAddress
                      : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  isRequired
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`phoneNumber`}
                  placeholder=""
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Phone Number"
                  error={
                    formik.touched.phoneNumber ? formik.errors.phoneNumber : ''
                  }
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  isRequired
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <GenderField
                  gender={formik.values.gender}
                  onSelect={gender => formik.setFieldValue(`gender`, gender)}
                  isRequired
                />
              </div>
            </div>
          </div>

          <div className=" flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="flex w-full items-end justify-end gap-4">
            <Button
              variant="primary"
              size="custom"
              className="mt-5 w-[9rem] rounded p-2.5"
              label="Update Spouse"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
